import React, { useState } from 'react'
import { AlertCircle, ArrowLeft, Check, Copy, KeyRound, Mail, ShieldCheck } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import type { MfaChallenge, MfaMethod } from '../services/authService'

const inputClass = 'w-full px-3 py-3 rounded-xl border border-slate-300 bg-white text-slate-900 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20'

/** Way out of a second-factor step: back to the username/password form. */
export const BackToSignIn: React.FC<{ onClick?: () => void; disabled?: boolean }> = ({ onClick, disabled }) => {
  const { t } = useTranslation()
  if (!onClick) return null
  return (
    <button type="button" onClick={onClick} disabled={disabled}
      className="flex w-full items-center justify-center gap-1.5 text-sm font-medium text-slate-500 hover:text-slate-800 disabled:opacity-50">
      <ArrowLeft size={15} /> {t('mfa_back_to_login', { defaultValue: 'Back to sign-in' })}
    </button>
  )
}

export const AuthError: React.FC<{ message?: string | null }> = ({ message }) => message ? (
  <div className="flex items-center gap-2 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
    <AlertCircle size={17} className="flex-shrink-0" /> {message}
  </div>
) : null

export const TotpEnrollmentForm: React.FC<{
  challenge: MfaChallenge
  busy?: boolean
  error?: string | null
  onConfirm: (code: string) => void
}> = ({ challenge, busy, error, onConfirm }) => {
  const { t } = useTranslation()
  const [code, setCode] = useState('')
  const setup = challenge.setup
  return (
    <form onSubmit={event => { event.preventDefault(); onConfirm(code.trim()) }} className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t('mfa_setup_title')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('mfa_setup_description')}</p>
      </div>
      {setup?.qr_data_url && (
        <div className="flex justify-center rounded-xl border border-slate-200 bg-white p-3">
          <img src={setup.qr_data_url} alt={t('mfa_qr_alt')} className="h-48 w-48 object-contain" />
        </div>
      )}
      {setup?.secret && (
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
          <div className="text-xs font-medium text-slate-500">{t('mfa_manual_secret')}</div>
          <code className="mt-1 block break-all select-all text-sm font-semibold text-slate-800">{setup.secret}</code>
        </div>
      )}
      <AuthError message={error} />
      <label className="block text-sm font-medium text-slate-700">
        {t('mfa_authenticator_code')}
        <input value={code} onChange={event => setCode(event.target.value)}
          inputMode="numeric" autoComplete="one-time-code" maxLength={8} className={`${inputClass} mt-2`}
          placeholder="000000" required autoFocus />
      </label>
      <button disabled={busy || code.trim().length < 6}
        className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? t('loading') : t('mfa_confirm_setup')}
      </button>
    </form>
  )
}

export const MfaEnrollmentForm: React.FC<{
  challenge: MfaChallenge
  busy?: boolean
  error?: string | null
  onConfirm: (method: Exclude<MfaMethod, 'recovery'>, code: string) => void
  onSendEmail: () => Promise<boolean>
  onCancel?: () => void
}> = ({ challenge, busy, error, onConfirm, onSendEmail, onCancel }) => {
  const { t } = useTranslation()
  const available = challenge.methods.filter((method): method is Exclude<MfaMethod, 'recovery'> =>
    method === 'totp' || method === 'email')
  const [method, setMethod] = useState<Exclude<MfaMethod, 'recovery'> | null>(
    available.length === 1 ? available[0] : null
  )
  const [code, setCode] = useState('')
  const [emailSent, setEmailSent] = useState(false)
  const [sending, setSending] = useState(false)

  const chooseMethod = async (next: Exclude<MfaMethod, 'recovery'>) => {
    setMethod(next)
    setCode('')
    if (next !== 'email') return
    setSending(true)
    try {
      setEmailSent(await onSendEmail())
    } finally {
      setSending(false)
    }
  }

  return (
    <form onSubmit={event => {
      event.preventDefault()
      if (method) onConfirm(method, code.trim())
    }} className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t('mfa_choose_title')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('mfa_choose_description')}</p>
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {available.map(item => (
          <button key={item} type="button" onClick={() => chooseMethod(item)}
            className={`rounded-xl border p-4 text-left transition ${
              method === item ? 'border-blue-400 bg-blue-50 ring-2 ring-blue-100' : 'border-slate-200 hover:bg-slate-50'
            }`}>
            <span className="flex items-center gap-2 font-semibold text-slate-800">
              {item === 'email' ? <Mail size={18} /> : <ShieldCheck size={18} />}
              {t(`mfa_method_${item}`)}
            </span>
            <span className="mt-2 block text-xs leading-5 text-slate-500">
              {t(item === 'email' ? 'mfa_choose_email_description' : 'mfa_choose_totp_description')}
            </span>
          </button>
        ))}
      </div>
      {method === 'totp' && <>
        {challenge.setup?.qr_data_url && (
          <div className="flex justify-center rounded-xl border border-slate-200 bg-white p-3">
            <img src={challenge.setup.qr_data_url} alt={t('mfa_qr_alt')} className="h-48 w-48 object-contain" />
          </div>
        )}
        {challenge.setup?.secret && (
          <div className="rounded-xl border border-slate-200 bg-slate-50 p-3">
            <div className="text-xs font-medium text-slate-500">{t('mfa_manual_secret')}</div>
            <code className="mt-1 block break-all select-all text-sm font-semibold text-slate-800">{challenge.setup.secret}</code>
          </div>
        )}
      </>}
      {method === 'email' && (
        <div className="rounded-xl border border-blue-100 bg-blue-50 p-3 text-xs text-blue-800">
          {sending ? t('loading') : emailSent ? t('mfa_email_sent') : t('mfa_email_send_failed')}
          {!sending && <button type="button" onClick={() => chooseMethod('email')}
            className="ml-2 font-semibold underline">{t('mfa_email_resend')}</button>}
        </div>
      )}
      <AuthError message={error} />
      {method && (
        <label className="block text-sm font-medium text-slate-700">
          {method === 'totp' ? t('mfa_authenticator_code') : t('mfa_verification_code')}
          <input value={code} onChange={event => setCode(event.target.value)}
            inputMode="numeric" autoComplete="one-time-code" maxLength={8} className={`${inputClass} mt-2`}
            placeholder="000000" required autoFocus />
        </label>
      )}
      <button disabled={busy || sending || !method || code.trim().length < 6 || (method === 'email' && !emailSent)}
        className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? t('loading') : t('mfa_confirm_setup')}
      </button>
      <BackToSignIn onClick={onCancel} disabled={busy || sending} />
    </form>
  )
}

export const MfaVerificationForm: React.FC<{
  challenge: MfaChallenge
  busy?: boolean
  error?: string | null
  onVerify: (method: MfaMethod, code: string) => void
  onSendEmail: () => Promise<boolean>
  onCancel?: () => void
}> = ({ challenge, busy, error, onVerify, onSendEmail, onCancel }) => {
  const { t } = useTranslation()
  const methods = challenge.methods.length ? challenge.methods : ['totp'] as MfaMethod[]
  const [method, setMethod] = useState<MfaMethod>(methods.includes('totp') ? 'totp' : methods[0])
  const [code, setCode] = useState('')
  const [emailSent, setEmailSent] = useState(false)

  const chooseMethod = async (next: MfaMethod) => {
    setMethod(next)
    setCode('')
    if (next === 'email') setEmailSent(await onSendEmail())
  }

  return (
    <form onSubmit={event => { event.preventDefault(); onVerify(method, code.trim()) }} className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t('mfa_verify_title')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('mfa_verify_description')}</p>
      </div>
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-3">
        {methods.map(item => (
          <button key={item} type="button" onClick={() => chooseMethod(item)}
            className={`flex items-center justify-center gap-1.5 rounded-lg border px-2 py-2 text-xs font-medium ${
              method === item ? 'border-blue-300 bg-blue-50 text-blue-700' : 'border-slate-200 text-slate-600 hover:bg-slate-50'
            }`}>
            {item === 'email' ? <Mail size={14} /> : item === 'recovery' ? <KeyRound size={14} /> : <ShieldCheck size={14} />}
            {t(`mfa_method_${item}`)}
          </button>
        ))}
      </div>
      {method === 'email' && emailSent && <p className="text-xs text-emerald-700">{t('mfa_email_sent')}</p>}
      <AuthError message={error} />
      <label className="block text-sm font-medium text-slate-700">
        {method === 'recovery' ? t('mfa_recovery_code') : t('mfa_verification_code')}
        <input value={code} onChange={event => setCode(event.target.value)}
          autoComplete="one-time-code" className={`${inputClass} mt-2`}
          placeholder={method === 'recovery' ? 'XXXX-XXXX-XXXX' : '000000'} required autoFocus />
      </label>
      <button disabled={busy || !code.trim()}
        className="w-full rounded-xl bg-gradient-to-r from-blue-600 to-indigo-700 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {busy ? t('loading') : t('mfa_verify_action')}
      </button>
      <BackToSignIn onClick={onCancel} disabled={busy} />
    </form>
  )
}

export const RecoveryCodesPanel: React.FC<{
  codes: string[]
  onContinue: () => void
}> = ({ codes, onContinue }) => {
  const { t } = useTranslation()
  const [acknowledged, setAcknowledged] = useState(false)
  const [copied, setCopied] = useState(false)
  const copy = async () => {
    await navigator.clipboard.writeText(codes.join('\n'))
    setCopied(true)
  }
  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-xl font-bold text-slate-900">{t('mfa_recovery_title')}</h2>
        <p className="mt-1 text-sm text-slate-500">{t('mfa_recovery_description')}</p>
      </div>
      <div className="grid grid-cols-2 gap-2 rounded-xl border border-amber-200 bg-amber-50 p-4">
        {codes.map(code => <code key={code} className="select-all text-center text-sm font-semibold text-amber-950">{code}</code>)}
      </div>
      <button type="button" onClick={copy}
        className="flex w-full items-center justify-center gap-2 rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50">
        {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? t('copied') : t('copy_codes')}
      </button>
      <label className="flex items-start gap-2 text-sm text-slate-700">
        <input type="checkbox" checked={acknowledged} onChange={event => setAcknowledged(event.target.checked)}
          className="mt-0.5 rounded border-slate-300 text-blue-600" />
        {t('mfa_recovery_acknowledge')}
      </label>
      <button disabled={!acknowledged} onClick={onContinue}
        className="w-full rounded-xl bg-blue-600 px-4 py-3 text-sm font-semibold text-white disabled:opacity-50">
        {t('continue')}
      </button>
    </div>
  )
}
